// Grid setup (slice 07; design/09 §3–§5): a mode the DM can re-enter, in
// the scene section of the properties panel. Per axis: the source edge and
// squares-across OR square-size, each deriving the other; the selected
// line's exact position with nudge buttons, its lock; a reset. Appearance:
// colour, opacity, visibility. Every change is one `grid.set` event — the
// finished result, which is what players see.
import { useEffect, useState } from 'react';
import {
  GRID_LIMITS, isGridless, squareLabel, axisByPitch, axisBySquares, boardExtent, deriveAxis, dragLine, lockLine, resetAxis, toBoard, unlockLine,
  type Edge, type Grid, type GridAxis,
} from '@dragons-table/vtt-shared';
import { gridEditor, type GridEditorState } from '../../board/gridEditor';
import { detectMessage, detectOnMap, workerRunner, type DetectRunner } from '../../board/detect';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useBoardMap } from '../../board/sceneMap';
import { pressWord } from '../device';
import { BTN } from '../ui';

const btn = BTN;
const PROVISIONAL_TIP = 'Unlocked adjustments are provisional — lock as you go, or a lock elsewhere recomputes this line.';

export function useGridEditor(): GridEditorState {
  const [s, setS] = useState(gridEditor.get());
  useEffect(() => gridEditor.subscribe(setS), []);
  return s;
}

function AxisSetup({ label, axisKey, grid, extent, commit }: { label: string; axisKey: 'x' | 'y'; grid: Grid; extent: number; commit: (g: Grid) => void }) {
  const axis = grid[axisKey];
  const editor = useGridEditor();
  const edges: Edge[] = axisKey === 'x' ? ['left', 'right'] : ['top', 'bottom'];
  const [squares, setSquares] = useState(String(Math.round(extent / axis.basePitch)));
  const [pitch, setPitch] = useState(String(Math.round(axis.basePitch * 100) / 100));
  useEffect(() => { setSquares(String(Math.round(extent / axis.basePitch))); setPitch(String(Math.round(axis.basePitch * 100) / 100)); }, [axis.basePitch, extent]);
  const lines = deriveAxis(axis, extent);
  const selected = editor.selected?.axis === axisKey ? lines.find((l) => l.index === editor.selected!.index) ?? null : null;
  const [typed, setTyped] = useState('');
  const [exact, setExact] = useState(false);
  const exactOpen = exact || selected !== null;
  useEffect(() => setTyped(selected ? String(Math.round(selected.pos * 100) / 100) : ''), [selected?.index, selected?.pos]);

  const withAxis = (next: GridAxis) => commit({ ...grid, [axisKey]: next });
  // Typing or nudging a line is the same adjustment as dragging it (issue 1226, Tom's A; design/09 §4: "the padlock is
  // always an explicit click"): an unlocked line stays unlocked and provisional, and only the padlock locks it. A locked
  // line (detection locks every line) is corrected exactly here and stays locked: nothing locks or unlocks itself.
  const setPos = (pos: number) => {
    if (!selected || !Number.isFinite(pos)) return;
    withAxis(selected.locked ? lockLine(axis, selected.index, pos) : dragLine(axis, selected.index, pos));
  };
  const posTip = selected?.locked ? 'Locked: typed here it moves and stays locked; a drag or a lock elsewhere never moves it.' : PROVISIONAL_TIP;

  return (
    <fieldset className="space-y-2 rounded border border-stone-700 p-2" data-testid={`grid-axis-${axisKey}`}>
      <legend className="vtt-section-head px-1">{label}</legend>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-xs">
          <span className="text-stone-400">Counted from</span>
          <select aria-label={`${label}: counted from`} className="mt-0.5 block rounded bg-stone-800 px-2 py-1 text-sm" value={axis.edge}
            onChange={(e) => withAxis({ ...axis, edge: e.target.value as Edge })}>
            {edges.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-stone-400">Squares across</span>
          <input aria-label={`${label}: squares across`} type="number" min={1} max={400} className="mt-0.5 block w-20 rounded bg-stone-800 px-2 py-1 text-sm" value={squares}
            onChange={(e) => { setSquares(e.target.value); const n = Number(e.target.value); if (n > 0) setPitch(String(Math.round((extent / n) * 100) / 100)); }} />
        </label>
        <label className="block text-xs">
          <span className="text-stone-400">Square size (px)</span>
          <input aria-label={`${label}: square size`} type="number" min={4} max={4000} step="any" className="mt-0.5 block w-20 rounded bg-stone-800 px-2 py-1 text-sm" value={pitch}
            onChange={(e) => { setPitch(e.target.value); const p = Number(e.target.value); if (p > 0) setSquares(String(Math.round(extent / p))); }} />
        </label>
        <button type="button" className={btn} aria-label={`${label}: apply`} onClick={() => {
          const p = Number(pitch); const n = Number(squares);
          if (p >= 4 && p <= 4000) withAxis({ ...axisByPitch(axis.edge, p) });
          else if (n >= 1) withAxis(axisBySquares(axis.edge, n, extent));
        }}>
          Apply
        </button>
        <button type="button" className={btn} aria-label={`${label}: reset`} onClick={() => withAxis(resetAxis(axis))}>Reset axis</button>
      </div>
      <p className="text-xs text-stone-500">{lines.length} lines, {lines.filter((l) => l.locked).length} locked{axis.provisional ? ', one provisional' : ''}.</p>
      {/* Exact per-line editing is a second step (issue 1024): shown as the first thing, it read as "set every line", and
          a DM asked whether squares could differ in size. It opens itself when a line is picked on the board. */}
      <button type="button" className={btn} aria-expanded={exactOpen} aria-label={`${label}: adjust one line exactly`}
        onClick={() => { if (exactOpen) { setExact(false); if (selected) gridEditor.select(null); } else setExact(true); }}>
        {exactOpen ? 'Done adjusting lines' : 'Adjust one line exactly…'}
      </button>
      {exactOpen && (<>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-xs">
          <span className="text-stone-400">Line</span>
          <input aria-label={`${label}: line`} type="number" min={0} className="mt-0.5 block w-16 rounded bg-stone-800 px-2 py-1 text-sm"
            value={selected ? selected.index : ''} onChange={(e) => { const i = Number(e.target.value); if (Number.isInteger(i) && i >= 0) gridEditor.select({ axis: axisKey, index: i }); }} />
        </label>
        <label className="block text-xs" title={posTip}>
          <span className="text-stone-400">Position (px from {axis.edge})</span>
          <input aria-label={`${label}: position`} type="number" step="any" className="mt-0.5 block w-24 rounded bg-stone-800 px-2 py-1 text-sm" value={typed} disabled={!selected}
            onChange={(e) => setTyped(e.target.value)} onBlur={() => { const p = Number(typed); if (selected && Number.isFinite(p) && p !== selected.pos) setPos(p); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { const p = Number(typed); if (Number.isFinite(p)) setPos(p); } }} />
        </label>
        <button type="button" className={btn} title={posTip} aria-label={`${label}: nudge towards the edge`} disabled={!selected} onClick={() => selected && setPos(selected.pos - 1)}>−1</button>
        <button type="button" className={btn} title={posTip} aria-label={`${label}: nudge away from the edge`} disabled={!selected} onClick={() => selected && setPos(selected.pos + 1)}>+1</button>
        {selected && (
          <button type="button" className={btn} aria-pressed={selected.locked} aria-label={`${label}: ${selected.locked ? 'unlock' : 'lock'} line ${selected.index}`}
            title={selected.locked ? 'Locked: nothing moves it. Unlocking returns it to its derived position.' : 'Lock this line where it is.'}
            onClick={() => withAxis(selected.locked ? unlockLine(axis, selected.index) : lockLine(axis, selected.index, selected.pos))}>
            {selected.locked ? '🔒 Locked' : '🔓 Unlocked'}
          </button>
        )}
      </div>
      {selected && <p className="text-xs text-stone-500">Board {axisKey === 'x' ? 'x' : 'y'} = {Math.round(toBoard(axis.edge, selected.pos, extent) * 100) / 100}.</p>}
      </>)}
    </fieldset>
  );
}

/** Areas left ungridded (issue 1035, GRID-008): drawn on the board as a drag, listed here, each removed alone. */
function BlankAreas({ grid, commit }: { grid: Grid; commit: (g: Grid) => void }) {
  const editor = useGridEditor();
  const blanks = grid.blank ?? [];
  const round = (n: number) => Math.round(n);
  const remove = (k: number) => { const { blank: _b, ...rest } = grid; const left = blanks.filter((_, i) => i !== k); commit(left.length ? { ...rest, blank: left } : rest); };
  return (
    <fieldset className="space-y-1 rounded border border-stone-700 p-2" data-testid="grid-blank">
      <legend className="vtt-section-head px-1">Ungridded areas</legend>
      <p className="text-xs text-stone-500">No lines are drawn in these, such as a legend down one side. Tokens still snap there.</p>
      <button type="button" className={btn} aria-pressed={!!editor.drawingBlank} disabled={blanks.length >= GRID_LIMITS.maxBlank}
        onClick={() => gridEditor.drawBlank(!editor.drawingBlank)}>{editor.drawingBlank ? 'Drag on the board… (click to stop)' : 'Draw an ungridded area'}</button>
      {blanks.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {blanks.map((b, k) => (
            <li key={k} className="flex items-center gap-2">
              <span>{round(b.x)}, {round(b.y)} · {round(b.width)} × {round(b.height)}</span>
              <button type="button" className={btn} aria-label={`Remove ungridded area ${k + 1}`} onClick={() => remove(k)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

export function GridSetup({ runner = workerRunner }: { runner?: DetectRunner } = {}) {
  const { table, onResult: _unused } = useShell();
  const boardMap = useBoardMap(); // the scene on this screen (issue 834)
  const { store, act, systemRules } = useTableState();
  const editor = useGridEditor();
  const grid = store.state.grid;
  const extent = boardExtent(boardMap);
  const commit = (g: Grid) => act({ kind: 'grid.set', grid: g });
  useEffect(() => () => gridEditor.reset(), []);
  // Detect, then correct (design/09 §7): the map is read in a worker; what it finds becomes locked lines, one grid.set.
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState('');
  const detect = async () => {
    if (!boardMap) return;
    setDetecting(true); setDetected('');
    try {
      const outcome = await detectOnMap(boardMap.id, grid, runner);
      setDetected(detectMessage(outcome));
      if (outcome.kind === 'placed') commit(outcome.grid);
    } catch (err) {
      // Whatever went wrong, the button comes back and the DM is told: the manual path is always there.
      setDetected(detectMessage({ kind: 'none', reason: err instanceof Error ? err.message : 'the map could not be read' }));
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="grid-setup">
      <div className="flex items-center gap-2">
        <button type="button" className={btn} aria-pressed={editor.setupOn} onClick={() => gridEditor.setSetup(!editor.setupOn)}>
          {editor.setupOn ? 'Leave grid setup' : 'Grid setup…'}
        </button>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" aria-label="Grid visible" checked={grid.appearance.visible}
          onChange={(e) => commit({ ...grid, appearance: { ...grid.appearance, visible: e.target.checked } })} /> Visible</label>
      </div>
      {!editor.setupOn && boardMap && !isGridless(grid) && grid.x.anchors.length < 2 && grid.y.anchors.length < 2 && (
        // A map whose grid was never fitted (issue 1024): the playtest's tokens sat over the printed squares, and the DM
        // did not know setup existed. The offer goes once anything is locked.
        <p className="text-xs text-stone-300" data-testid="grid-offer">
          This map's grid has not been fitted to it.{' '}
          <button type="button" className={`${btn} border-amber-600`} onClick={() => { gridEditor.setSetup(true); void detect(); }}>Detect the grid</button>
        </p>
      )}
      {editor.setupOn && (
        <div className="space-y-2">
          <p className="text-xs text-stone-300" data-testid="grid-lead">Squares stay the same size. On a map with its own grid, lock two or three lines on each axis where the map's lines are (drag a line on the board, then {pressWord().toLowerCase()} its padlock): the lines between and beyond them are spaced evenly, so a hand-drawn map that drifts is followed too. Or let Detect find them first.</p>
          <div className="flex flex-wrap items-center gap-2" data-testid="grid-detect">
            <button type="button" className={btn} disabled={!boardMap || detecting} onClick={() => void detect()}
              title={boardMap ? 'Read the map for printed gridlines and place them, locked. Nothing else changes; you correct what it gets wrong.' : 'There is no map to read: the empty grid is set by hand.'}>
              {detecting ? 'Reading the map…' : 'Detect the grid'}
            </button>
            {detected && <span className="text-xs text-stone-300" role="status" data-testid="grid-detected">{detected}</span>}
          </div>
          {/* No grid at all (issue 1035, GRID-005): the axes stay, as the scale — a square is the system's ("5 ft"), or one square of the ruler with none. */}
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" aria-label="No grid on this map" checked={isGridless(grid)}
            onChange={(e) => { const { mode: _m, ...rest } = grid; commit(e.target.checked ? { ...rest, mode: 'none' } : rest); }} /> No grid on this map</label>
          {isGridless(grid) && (
            <p className="text-xs text-stone-400" data-testid="gridless-note">
              Nothing snaps; the ruler measures straight distances. A square of {Math.round(grid.x.basePitch * 100) / 100} px is {systemRules.distance ? squareLabel(systemRules.distance) : 'one square on the ruler'}: set the square size below to this map's scale.
            </p>
          )}
          {!isGridless(grid) && <BlankAreas grid={grid} commit={commit} />}
          <AxisSetup label="Vertical lines" axisKey="x" grid={grid} extent={extent.width} commit={commit} />
          <AxisSetup label="Horizontal lines" axisKey="y" grid={grid} extent={extent.height} commit={commit} />
          <fieldset className="flex flex-wrap items-end gap-3 rounded border border-stone-700 p-2">
            <legend className="vtt-section-head px-1">Appearance</legend>
            <label className="block text-xs">
              <span className="text-stone-400">Colour</span>
              <input aria-label="Grid colour" type="color" className="mt-0.5 block h-7 w-12 rounded bg-stone-800" value={grid.appearance.colour}
                onChange={(e) => commit({ ...grid, appearance: { ...grid.appearance, colour: e.target.value } })} />
            </label>
            <label className="block text-xs">
              <span className="text-stone-400">Opacity {Math.round(grid.appearance.opacity * 100)}%</span>
              <input aria-label="Grid opacity" type="range" min={0} max={1} step={0.05} className="mt-0.5 block w-28" value={grid.appearance.opacity}
                onChange={(e) => commit({ ...grid, appearance: { ...grid.appearance, opacity: Number(e.target.value) } })} />
            </label>
          </fieldset>
        </div>
      )}
    </div>
  );
}
